<template>
  <Dialog v-model="visible" :title="dialogTitle" width="960px">
    <el-alert
      class="mb-12px"
      type="info"
      :closable="false"
      show-icon
      title="外发货品须填写供货结算价"
      description="每个 SKU 的「供货结算价」必填（大于 0）。提交审核与挂店后按下单快照出账；仅填成本价无法出账。"
    />
    <el-alert
      v-if="detail?.auditRemark && detail.status === 3"
      :title="'驳回原因：' + detail.auditRemark"
      type="warning"
      class="mb-12px"
      show-icon
      :closable="false"
    />
    <el-form
      v-if="!readonly || true"
      ref="supplierFormRef"
      :model="meta"
      :rules="metaRules"
      label-width="120px"
      class="mb-12px"
      :disabled="readonly || !!meta.id"
    >
      <el-form-item label="供应商" prop="supplierId">
        <el-select
          v-model="meta.supplierId"
          filterable
          class="!w-320px"
          :disabled="!!meta.id || readonly"
          placeholder="请选择供应商"
        >
          <el-option v-for="s in suppliers" :key="s.id" :label="s.name" :value="s.id" />
        </el-select>
      </el-form-item>
    </el-form>
    <el-tabs v-model="activeName" v-loading="loading">
      <el-tab-pane label="基础设置" name="info">
        <AdminInfoForm
          ref="infoRef"
          v-model:activeName="activeName"
          :is-detail="readonly"
          :propFormData="formData"
          pool-mode
        />
      </el-tab-pane>
      <el-tab-pane label="价格库存" name="sku">
        <SkuForm
          ref="skuRef"
          v-model:activeName="activeName"
          :is-detail="readonly"
          :propFormData="formData"
          mode="admin"
        />
      </el-tab-pane>
      <el-tab-pane label="物流设置" name="delivery">
        <DeliveryForm
          ref="deliveryRef"
          v-model:activeName="activeName"
          :is-detail="readonly"
          :propFormData="formData"
          skip-template
        />
      </el-tab-pane>
      <el-tab-pane label="商品详情" name="description">
        <DescriptionForm
          ref="descriptionRef"
          v-model:activeName="activeName"
          :is-detail="readonly"
          :propFormData="formData"
        />
      </el-tab-pane>
      <el-tab-pane label="其它设置" name="other">
        <OtherForm
          ref="otherRef"
          v-model:activeName="activeName"
          :is-detail="readonly"
          :propFormData="formData"
        />
      </el-tab-pane>
    </el-tabs>
    <template #footer>
      <el-button @click="visible = false">取消</el-button>
      <el-button v-if="!readonly" :loading="loading" @click="save(false)">保存草稿</el-button>
      <el-button v-if="!readonly" :loading="loading" type="primary" @click="save(true)">
        提交审核
      </el-button>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { cloneDeep } from 'lodash-es'
import { convertToInteger, formatToFraction } from '@/utils'
import { isEmpty } from '@/utils/is'
import type { Spu } from '@/api/mall/product/spu'
import { getSimpleSupplierList } from '@/api/mall/product/supplier'
import { getPoolDraft, savePoolDraft, submitPoolDraft } from '@/api/mall/product/pool'
import AdminInfoForm from '@/views/mall/product/spu/form/InfoForm.vue'
import SkuForm from '@/views/mall/product/spu/form/SkuForm.vue'
import DeliveryForm from '@/views/mall/product/spu/form/DeliveryForm.vue'
import DescriptionForm from '@/views/mall/product/spu/form/DescriptionForm.vue'
import OtherForm from '@/views/mall/product/spu/form/OtherForm.vue'

defineOptions({ name: 'AdminPoolDraftForm' })
const emit = defineEmits(['success'])
const message = useMessage()

const visible = ref(false)
const readonly = ref(false)
const loading = ref(false)
const activeName = ref('info')
const suppliers = ref<{ id: number; name: string }[]>([])
const detail = ref<any>()
const supplierFormRef = ref()
const infoRef = ref()
const skuRef = ref()
const deliveryRef = ref()
const descriptionRef = ref()
const otherRef = ref()

const meta = reactive<{ id?: number; supplierId?: number }>({
  id: undefined,
  supplierId: undefined
})
const metaRules = {
  supplierId: [{ required: true, message: '请选择供应商', trigger: 'change' }]
}

const formData = ref<Spu>({
  name: '',
  categoryId: undefined,
  keyword: '',
  picUrl: '',
  sliderPicUrls: [],
  introduction: '',
  deliveryTypes: [],
  brandId: undefined,
  productSource: 2, // 货池挂店即为外发，展示并校验供货结算价
  specType: false,
  subCommissionType: false,
  skus: [
    {
      name: '',
      price: 0,
      marketPrice: undefined,
      costPrice: undefined,
      supplierSettlePrice: undefined,
      barCode: '',
      picUrl: '',
      stock: 0,
      weight: undefined,
      volume: undefined,
      firstBrokeragePercent: 0,
      secondBrokeragePercent: 0
    }
  ],
  description: '',
  sort: 0,
  giveIntegral: 0,
  virtualSalesCount: 0,
  guaranteeIds: []
})

const dialogTitle = computed(() => (readonly.value ? '查看货品' : '代供上传新品'))

const resetForm = () => {
  meta.id = undefined
  meta.supplierId = undefined
  activeName.value = 'info'
  formData.value = {
    name: '',
    categoryId: undefined,
    keyword: '',
    picUrl: '',
    sliderPicUrls: [],
    introduction: '',
    deliveryTypes: [],
    brandId: undefined,
    productSource: 2,
    specType: false,
    subCommissionType: false,
    skus: [
      {
        name: '',
        price: 0,
        marketPrice: undefined,
        costPrice: undefined,
        supplierSettlePrice: undefined,
        barCode: '',
        picUrl: '',
        stock: 0,
        weight: undefined,
        volume: undefined,
        firstBrokeragePercent: 0,
        secondBrokeragePercent: 0
      }
    ],
    description: '',
    sort: 0,
    giveIntegral: 0,
    virtualSalesCount: 0,
    guaranteeIds: []
  }
}

const open = async (id?: number, isReadonly = false) => {
  readonly.value = isReadonly
  detail.value = undefined
  resetForm()
  suppliers.value = (await getSimpleSupplierList()) || []
  if (id) {
    loading.value = true
    try {
      const data = await getPoolDraft(id)
      detail.value = data
      meta.id = data.id
      meta.supplierId = data.supplierId
      data.skus?.forEach((item: any) => {
        item.price = formatToFraction(item.price)
        item.marketPrice =
          item.marketPrice != null ? formatToFraction(item.marketPrice) : undefined
        item.costPrice = item.costPrice != null ? formatToFraction(item.costPrice) : undefined
        item.supplierSettlePrice =
          item.supplierSettlePrice != null ? formatToFraction(item.supplierSettlePrice) : undefined
        item.firstBrokeragePercent = item.firstBrokeragePercent ?? 0
        item.secondBrokeragePercent = item.secondBrokeragePercent ?? 0
      })
      formData.value = {
        ...formData.value,
        ...data,
        productSource: 2,
        guaranteeIds: data.guaranteeIds || []
      }
    } finally {
      loading.value = false
    }
  }
  visible.value = true
}

const buildPayload = () => {
  const deepCopy = cloneDeep(unref(formData.value)) as any
  if (isEmpty(deepCopy.name)) {
    throw new Error('商品名称不能为空')
  }
  deepCopy.skus!.forEach((item: any) => {
    item.name = deepCopy.name
    item.price = convertToInteger(item.price)
    item.marketPrice =
      item.marketPrice != null && item.marketPrice !== ''
        ? convertToInteger(item.marketPrice)
        : undefined
    item.costPrice =
      item.costPrice != null && item.costPrice !== ''
        ? convertToInteger(item.costPrice)
        : undefined
    item.supplierSettlePrice =
      item.supplierSettlePrice != null && item.supplierSettlePrice !== ''
        ? convertToInteger(item.supplierSettlePrice)
        : undefined
    item.firstBrokeragePercent = Number(item.firstBrokeragePercent ?? 0)
    item.secondBrokeragePercent = Number(item.secondBrokeragePercent ?? 0)
  })
  const newSliderPicUrls: any[] = []
  deepCopy.sliderPicUrls!.forEach((item: any) => {
    typeof item === 'object' ? newSliderPicUrls.push(item.url) : newSliderPicUrls.push(item)
  })
  return {
    id: meta.id,
    supplierId: meta.supplierId,
    name: deepCopy.name,
    keyword: deepCopy.keyword,
    introduction: deepCopy.introduction,
    description: deepCopy.description,
    categoryId: deepCopy.categoryId,
    brandId: deepCopy.brandId,
    picUrl: deepCopy.picUrl,
    sliderPicUrls: newSliderPicUrls,
    sort: deepCopy.sort ?? 0,
    specType: deepCopy.specType,
    deliveryTypes: deepCopy.deliveryTypes,
    giveIntegral: deepCopy.giveIntegral ?? 0,
    subCommissionType: deepCopy.subCommissionType,
    skus: deepCopy.skus
  }
}

const save = async (submit: boolean) => {
  const ok = await supplierFormRef.value?.validate().catch(() => false)
  if (!ok) return
  loading.value = true
  try {
    await unref(infoRef)?.validate()
    await unref(skuRef)?.validate()
    await unref(deliveryRef)?.validate()
    await unref(descriptionRef)?.validate()
    await unref(otherRef)?.validate()
    const payload = buildPayload()
    if (submit) {
      await submitPoolDraft(payload)
      message.success('已提交审核')
    } else {
      await savePoolDraft(payload)
      message.success('已保存草稿')
    }
    visible.value = false
    emit('success')
  } catch (e: any) {
    if (e?.message && !e?.response) {
      message.error(e.message)
    }
  } finally {
    loading.value = false
  }
}

defineExpose({ open })
</script>
